import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { User, Lock, Activity, Calendar, Video, ClipboardList, Save, KeyRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending:  'bg-amber-100 text-amber-700',
  active:   'bg-blue-100 text-blue-700',
  ended:    'bg-slate-100 text-slate-600',
  waiting:  'bg-amber-100 text-amber-700',
  cancelled:'bg-red-100 text-red-700',
  approved: 'bg-emerald-100 text-emerald-700',
  scheduled:'bg-blue-100 text-blue-700',
  confirmed:'bg-emerald-100 text-emerald-700',
};

/**
 * AdminUserEditDialog
 *
 * A single modal that lets an admin:
 *   1. View a staff user's full profile
 *   2. Edit their info (name/email/phone/specialty/biography/category/photo)
 *   3. Reset their password
 *   4. Browse their recent appointments + call queue history + activity log
 *
 * Backend endpoints:
 *   GET    /api/admin/users/{id}/activity
 *   PUT    /api/admin/users/{id}
 *   PUT    /api/admin/users/{id}/password
 */
export const AdminUserEditDialog = ({ open, onOpenChange, userId, onSaved }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [activity, setActivity] = useState({ appointments: [], calls: [], activity_logs: [] });
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/admin/users/${userId}/activity`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        setProfile({
          full_name:      r.data.user.full_name || '',
          email:          r.data.user.email || '',
          phone_number:   r.data.user.phone_number || '',
          specialty:      r.data.user.specialty || '',
          biography:      r.data.user.biography || '',
          category:       r.data.user.category || '',
          profile_photo:  r.data.user.profile_photo || '',
          is_available:   r.data.user.is_available !== false,
          role:           r.data.user.role || '',
        });
        setActivity({
          appointments: r.data.appointments || [],
          calls:        r.data.calls || [],
          activity_logs:r.data.activity_logs || [],
        });
      })
      .catch(() => toast.error(t('تعذّر تحميل بيانات المستخدم', 'Failed to load user data')))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, userId, t]);

  const updateField = (k, v) => setProfile((p) => (p ? { ...p, [k]: v } : p));

  const handleSaveProfile = async () => {
    if (!profile) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${userId}`, {
        full_name: profile.full_name,
        email: profile.email,
        phone_number: profile.phone_number,
        specialty: profile.specialty,
        biography: profile.biography,
        category: profile.category,
        profile_photo: profile.profile_photo,
        is_available: profile.is_available,
      }, { withCredentials: true });
      toast.success(t('تم تحديث الملف الشخصي', 'Profile updated'));
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل التحديث', 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!password || password.length < 4) {
      toast.error(t('كلمة المرور قصيرة جداً', 'Password too short'));
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${userId}/password`, { new_password: password }, { withCredentials: true });
      toast.success(t('تم تغيير كلمة المرور', 'Password reset successfully'));
      setPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل تغيير كلمة المرور', 'Failed to reset password'));
    } finally {
      setSaving(false);
    }
  };

  const roleLabel = (role) => {
    const map = {
      doctor:         t('طبيب', 'Doctor'),
      pharmacist:     t('صيدلي', 'Pharmacist'),
      physio_manager: t('مدير فيزيائي', 'Physio Manager'),
    };
    return map[role] || role;
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); }
    catch { return iso; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-user-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User size={18} className="text-[#1565c0]" />
            {t('إدارة المستخدم', 'Manage User')}
            {profile?.role && (
              <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                {roleLabel(profile.role)}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading || !profile ? (
          <div className="text-center py-10 text-slate-500 text-sm">
            {t('جاري التحميل...', 'Loading...')}
          </div>
        ) : (
          <Tabs defaultValue="profile" className="w-full">
            <TabsList className="grid grid-cols-3">
              <TabsTrigger value="profile" data-testid="tab-profile"><User size={14} className="mr-1" />{t('الملف', 'Profile')}</TabsTrigger>
              <TabsTrigger value="password" data-testid="tab-password"><Lock size={14} className="mr-1" />{t('كلمة المرور', 'Password')}</TabsTrigger>
              <TabsTrigger value="activity" data-testid="tab-activity"><Activity size={14} className="mr-1" />{t('النشاط', 'Activity')}</TabsTrigger>
            </TabsList>

            {/* Profile */}
            <TabsContent value="profile" className="space-y-3 pt-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t('الاسم الكامل', 'Full name')}</Label>
                  <Input value={profile.full_name} onChange={(e) => updateField('full_name', e.target.value)}
                         className="mt-1" data-testid="edit-full-name" />
                </div>
                <div>
                  <Label className="text-xs">{t('البريد الإلكتروني', 'Email')}</Label>
                  <Input type="email" value={profile.email} onChange={(e) => updateField('email', e.target.value)}
                         className="mt-1" data-testid="edit-email" />
                </div>
                <div>
                  <Label className="text-xs">{t('رقم الهاتف', 'Phone number')}</Label>
                  <Input value={profile.phone_number} onChange={(e) => updateField('phone_number', e.target.value)}
                         className="mt-1" data-testid="edit-phone" />
                </div>
                <div>
                  <Label className="text-xs">{t('التخصص', 'Specialty')}</Label>
                  <Input value={profile.specialty} onChange={(e) => updateField('specialty', e.target.value)}
                         className="mt-1" data-testid="edit-specialty" />
                </div>
                {profile.role === 'doctor' && (
                  <div>
                    <Label className="text-xs">{t('الفئة', 'Category')}</Label>
                    <Select value={profile.category || ''} onValueChange={(v) => updateField('category', v)}>
                      <SelectTrigger className="mt-1" data-testid="edit-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="doctors">{t('أطباء', 'Doctors')}</SelectItem>
                        <SelectItem value="physiotherapy">{t('علاج طبيعي', 'Physiotherapy')}</SelectItem>
                        <SelectItem value="psychology">{t('علم النفس', 'Psychology')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs">{t('رابط صورة الملف', 'Profile photo URL')}</Label>
                  <Input value={profile.profile_photo} onChange={(e) => updateField('profile_photo', e.target.value)}
                         className="mt-1" data-testid="edit-photo" placeholder="https://..." />
                </div>
              </div>
              <div>
                <Label className="text-xs">{t('السيرة الذاتية', 'Biography')}</Label>
                <Textarea rows={3} value={profile.biography} onChange={(e) => updateField('biography', e.target.value)}
                          className="mt-1 resize-none" data-testid="edit-biography" />
              </div>
              <Button
                onClick={handleSaveProfile}
                disabled={saving}
                className="w-full bg-[#1565c0] hover:bg-[#0d47a1] font-bold h-11 rounded-full"
                data-testid="save-profile-btn"
              >
                <Save size={16} className="mr-1" />
                {saving ? t('جاري الحفظ...', 'Saving...') : t('حفظ التغييرات', 'Save Changes')}
              </Button>
            </TabsContent>

            {/* Password */}
            <TabsContent value="password" className="space-y-3 pt-3">
              <Card className="p-4 bg-amber-50 border-amber-200">
                <p className="text-xs text-amber-900 leading-relaxed">
                  {t(
                    'تعيين كلمة مرور جديدة سيؤدي إلى تسجيل خروج المستخدم من كل الجلسات الحالية عند دخوله التالي.',
                    'Setting a new password logs the user out of all current sessions on their next request.',
                  )}
                </p>
              </Card>
              <div>
                <Label className="text-xs">{t('كلمة المرور الجديدة', 'New password')}</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1"
                  data-testid="admin-new-password"
                  placeholder="********"
                />
              </div>
              <Button
                onClick={handleResetPassword}
                disabled={saving || !password}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold h-11 rounded-full"
                data-testid="admin-reset-password-btn"
              >
                <KeyRound size={16} className="mr-1" />
                {saving ? t('جاري...', 'Saving...') : t('إعادة تعيين كلمة المرور', 'Reset Password')}
              </Button>
            </TabsContent>

            {/* Activity */}
            <TabsContent value="activity" className="space-y-4 pt-3">
              <div>
                <h4 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1">
                  <Calendar size={14} /> {t('المواعيد', 'Appointments')} ({activity.appointments.length})
                </h4>
                {activity.appointments.length === 0 ? (
                  <p className="text-xs text-slate-500">{t('لا شيء', 'None')}</p>
                ) : (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="admin-activity-appts">
                    {activity.appointments.slice(0, 15).map((a) => (
                      <div key={a._id} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-1.5">
                        <span className="text-slate-700">{a.appointment_date} {a.appointment_time || ''}</span>
                        <span className={`px-2 py-0.5 rounded-full font-semibold ${STATUS_STYLES[a.status] || 'bg-slate-200 text-slate-600'}`}>
                          {a.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h4 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1">
                  <Video size={14} /> {t('المكالمات', 'Call queue')} ({activity.calls.length})
                </h4>
                {activity.calls.length === 0 ? (
                  <p className="text-xs text-slate-500">{t('لا شيء', 'None')}</p>
                ) : (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="admin-activity-calls">
                    {activity.calls.slice(0, 15).map((c) => (
                      <div key={c._id} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-1.5">
                        <span className="text-slate-700">{fmtDate(c.created_at)} — {c.call_type || 'call'}</span>
                        <span className={`px-2 py-0.5 rounded-full font-semibold ${STATUS_STYLES[c.status] || 'bg-slate-200 text-slate-600'}`}>
                          {c.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h4 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1">
                  <ClipboardList size={14} /> {t('سجل النشاط', 'Activity log')} ({activity.activity_logs.length})
                </h4>
                {activity.activity_logs.length === 0 ? (
                  <p className="text-xs text-slate-500">{t('لا شيء', 'None')}</p>
                ) : (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="admin-activity-logs">
                    {activity.activity_logs.slice(0, 15).map((lg) => (
                      <div key={lg._id} className="text-xs bg-slate-50 rounded-lg px-3 py-1.5">
                        <p className="text-slate-700 font-semibold">{lg.action}</p>
                        <p className="text-slate-500 mt-0.5">{fmtDate(lg.timestamp)} — {lg.details}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdminUserEditDialog;
